// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import ts from 'typescript';

export function isOptional(type: ts.Type) {
  if (!type.isUnionOrIntersection()) {
    return false;
  }
  return !!type.types.find(t => t.flags & ts.TypeFlags.Undefined);
}

export function isNullable(type: ts.Type) {
  if (!type.isUnionOrIntersection()) {
    return false;
  }
  return !!type.types.find(t => t.flags & ts.TypeFlags.Null);
}

export function unwrapNamespaceDeclaration(declaration: ts.Declaration | undefined) {
  if (!declaration) {
    return [];
  }
  const namespaceBlock = declaration.getChildren().find(node => node.kind === ts.SyntaxKind.ModuleBlock);
  if (!namespaceBlock) {
    return [];
  }
  const moduleContent = namespaceBlock.getChildren().find(node => node.kind === ts.SyntaxKind.SyntaxList);
  if (!moduleContent) {
    return [];
  }
  return moduleContent.getChildren();
}

function stripUndefined(typeString: string) {
  return typeString.replace(/\| undefined$/, '').trim();
}

export function stringifyType(type: ts.Type, checker: ts.TypeChecker) {
  return stripUndefined(
    checker.typeToString(
      type,
      undefined,
      ts.TypeFormatFlags.WriteArrayAsGenericType |
        ts.TypeFormatFlags.UseFullyQualifiedType |
        ts.TypeFormatFlags.UseAliasDefinedOutsideCurrentScope,
    ),
  );
}

function expandTags(extraTags: ReadonlyArray<ts.JSDocTag>) {
  return extraTags.map(tag => ({
    name: tag.tagName.text,
    text: ts.getTextOfJSDocComment(tag.comment),
  }));
}

export function getDescription(docComment: Array<ts.SymbolDisplayPart>, declaration: ts.Node) {
  return {
    text: docComment.length > 0 ? ts.displayPartsToString(docComment) : undefined,
    tags: expandTags(ts.getJSDocTags(declaration)),
  };
}

export function extractDeclaration(symbol: ts.Symbol) {
  const declarations = symbol.getDeclarations();
  if (!declarations || declarations.length === 0) {
    throw new Error(`No declaration found for symbol: ${symbol.getName()}`);
  }
  if (declarations.length > 1) {
    throw new Error(`Multiple declarations found for symbol: ${symbol.getName()}`);
  }
  return declarations[0];
}

export function tryExtractDeclaration(symbol: ts.Symbol): ts.Declaration | undefined {
  const declarations = symbol.getDeclarations();
  if (!declarations || declarations.length === 0) {
    return undefined;
  }
  if (declarations.length === 1) {
    return declarations[0];
  }
  // Multiple property signature declarations occur in discriminated unions where the same
  // property appears in multiple branches (e.g. `prop: SomeType` in one and `prop?: never` in
  // another). Keep the branches that aren't `never` and use that declaration when it's unambiguous.
  if (declarations.every(ts.isPropertySignature)) {
    const nonNever = declarations.filter(decl => decl.type?.kind !== ts.SyntaxKind.NeverKeyword);
    if (nonNever.length === 1) {
      return nonNever[0];
    }
  }
  throw new Error(`Multiple declarations found for symbol: ${symbol.getName()}`);
}

export function isOptionalSymbol(symbol: ts.Symbol): boolean {
  return !!(symbol.flags & ts.SymbolFlags.Optional);
}

export function printFlags(flags: number, mapping: Record<string, number | string>) {
  return Object.entries(mapping)
    .filter(([, value]) => typeof value === 'number')
    .filter(([, value]) => (value as number) & flags)
    .map(([key]) => key);
}

export function extractTypeArguments(type: ts.Type, checker: ts.TypeChecker) {
  const typeParameters = checker.getTypeArguments(type as ts.TypeReference);

  if (typeParameters.length <= 0) {
    return { typeName: stringifyType(type, checker) };
  }
  const symbol = type.getSymbol();
  if (!symbol) {
    throw new Error(`Unknown generic type without symbol: ${stringifyType(type, checker)}`);
  }
  return { typeParameters, typeName: checker.symbolToString(symbol) };
}
